import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { WhiteGloveLayout } from '@/sections/white-glove/white-glove-layout';
import { TrialLockedPanel } from '@/sections/white-glove/trial-locked-panel';
import { WgCalendarView } from '@/sections/white-glove/calendar/wg-calendar-view';
import { useWhiteGloveAccess } from '@/sections/white-glove/use-white-glove-access';
import { toViewMode, type CalendarViewMode } from '@/sections/calendar/calendar-shared';

import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * The calendar, which is a White Glove surface and nothing else.
 *
 * A calendar is a month somebody else planned: dates held against a production
 * lead time, approvals, gap filling, work scheduled to send. That is the service,
 * not a feature of the builder, so a brand that has not bought the service is
 * told what it is rather than handed a planner shaped like it.
 *
 * The brand's engagement decides, not the URL. `/white-glove/calendar` redirects
 * here.
 */
export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/calendar/')({
  component: CalendarPage,
  // The view mode is in the URL so a reload keeps it and a link carries it. It
  // is also how the redirect from the old month route lands on the folder.
  validateSearch: (search: Record<string, unknown>): { view?: CalendarViewMode } => ({
    view: toViewMode(search.view),
  }),
});

function CalendarPage() {
  const { t } = useTranslation('whiteGlove');
  const navigate = Route.useNavigate();
  const { view } = Route.useSearch();

  // Replace rather than push: flipping between a plan and a list is not a place
  // the back button should have to walk through.
  const onViewChange = (next: CalendarViewMode) =>
    navigate({ search: { view: next }, replace: true });
  const { enabled: managed, isTrial, isLoading } = useWhiteGloveAccess();

  // Wait rather than guess. Rendering the locked panel first and swapping once
  // the tier arrives would flash a paywall at a paying customer.
  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  // A trial is two designs, which come with no month to plan. Everyone without
  // a live engagement lands here: the page says what a calendar would be and
  // offers the way to have one, rather than handing over a planner that is a
  // different product they also have not bought.
  if (!managed || isTrial) {
    return (
      <TrialLockedPanel
        title={t('tabs.calendar', 'Calendar')}
        body={t(
          'trial.locked.calendar',
          'Your calendar is the month our team plans and keeps filled ahead of you.'
        )}
      />
    );
  }

  return (
    <WhiteGloveLayout title={t('tabs.calendar', 'Calendar')}>
      <WgCalendarView view={view ?? 'plan'} onViewChange={onViewChange} />
    </WhiteGloveLayout>
  );
}
